import React, { useCallback, useMemo, useState } from "react";
import { FinancialAccount } from "@common/types/financialAccount";
import { InvoiceSource } from "@common/types/invoice";
import { useEntities } from "@hooks/useEntities";
import {
    useFinancialAccounts,
    UseFinancialAccountsOptions,
} from "@hooks/useFinancialAccounts";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { FilterPickerList } from "@components/pageHeader/filters/FilterPickerList/FilterPickerList";
import {
    accountValue,
    buildAccountFilterRows,
    entityValue,
    KICK_VALUE,
    NO_ACCOUNT_VALUE,
    subaccountValue,
} from "./accountFilterRows";
import {
    AccountFilters,
    accountMatches,
    applyRowToggle,
    buildDisplayedEntityAccounts,
    findToggledRow,
} from "./accountFilterToggle";
import css from "./AggregatedAccountsFilter.module.scss";

interface AggregatedAccountsSearchProps extends UseFinancialAccountsOptions {
    filters: AccountFilters;
    onChange(value: AccountFilters): void;
    businessOnly?: boolean;
    accountFilterPredicate?: (account: FinancialAccount) => boolean;
    shouldShowNoAccountOption?: boolean;
    // Invoices page only: surfaces a virtual "Kick" entry (native/manual
    // invoices, which have no financialAccountId) as a selectable value in
    // the Account filter, alongside real connected financial accounts.
    shouldShowKickAccountOption?: boolean;
}

export const AggregatedAccountsFilter: React.FC<
    AggregatedAccountsSearchProps
> = ({
    filters,
    onChange,
    businessOnly,
    isSingleSelect,
    accountFilterPredicate,
    shouldShowNoAccountOption,
    shouldShowKickAccountOption,
    ...options
}) => {
    const [search, setSearch] = useState("");
    const entities = useEntities();
    const allBankAccounts = useFinancialAccounts({
        excludedAccountIds: filters.excludedAccountIds,
        ...options,
    });
    const bankAccounts = useMemo(
        () =>
            accountFilterPredicate
                ? allBankAccounts.filter(accountFilterPredicate)
                : allBankAccounts,
        [allBankAccounts, accountFilterPredicate],
    );

    const displayedEntityAccounts = useMemo(
        () =>
            buildDisplayedEntityAccounts({
                entities,
                accounts: bankAccounts,
                search,
                isBusinessOnly: !!businessOnly,
            }),
        [bankAccounts, entities, businessOnly, search],
    );

    const { items, rowByValue } = useMemo(
        () =>
            buildAccountFilterRows({
                entityAccounts: displayedEntityAccounts,
                shouldShowKickRow:
                    !!shouldShowKickAccountOption &&
                    "kick".includes(search.toLowerCase()),
                shouldShowNoAccountRow: !!shouldShowNoAccountOption && !search,
            }),
        [
            displayedEntityAccounts,
            search,
            shouldShowKickAccountOption,
            shouldShowNoAccountOption,
        ],
    );
    const entityValues = useMemo(
        () =>
            displayedEntityAccounts.map(({ entity }) => entityValue(entity.id)),
        [displayedEntityAccounts],
    );

    const isNoAccountSelected = filters.hasNoFinancialAccount ?? false;
    const isKickSelected =
        filters.invoiceSources?.includes(InvoiceSource.MANUAL) ?? false;

    const value = useMemo(() => {
        const selectedAccountIds = new Set(filters.accountIds ?? []);
        return [
            ...(isKickSelected ? [KICK_VALUE] : []),
            ...displayedEntityAccounts
                .filter(({ accounts }) =>
                    accounts.every(({ id }) => selectedAccountIds.has(id)),
                )
                .map(({ entity }) => entityValue(entity.id)),
            ...[...selectedAccountIds].map(accountValue),
            ...(filters.subaccountIds ?? []).map(subaccountValue),
            ...(isNoAccountSelected ? [NO_ACCOUNT_VALUE] : []),
        ];
    }, [
        displayedEntityAccounts,
        filters.accountIds,
        filters.subaccountIds,
        isKickSelected,
        isNoAccountSelected,
    ]);

    const handleValueChange = useCallback(
        (next: string[]) => {
            const row = findToggledRow({ value, next, rowByValue });
            const change =
                row === undefined
                    ? undefined
                    : applyRowToggle({
                          row,
                          filters,
                          isSingleSelect: !!isSingleSelect,
                      });
            if (change !== undefined) {
                onChange(change);
            }
        },
        [filters, isSingleSelect, onChange, rowByValue, value],
    );

    const matchesSearch = useCallback(
        (item: PickerItemData<string>, query: string) => {
            const row = rowByValue.get(item.value);
            if (row?.kind === "account") {
                return accountMatches(row.account, query);
            }
            return (
                row?.kind === "kick" &&
                item.label.toLowerCase().includes(query.toLowerCase())
            );
        },
        [rowByValue],
    );

    const reverseSelection = () => {
        const selectedAccountIds = new Set(filters.accountIds ?? []);
        onChange({
            accountIds: bankAccounts
                .filter((account) => !selectedAccountIds.has(account.id))
                .map(({ id }) => id),
            hasNoFinancialAccount: isNoAccountSelected ? undefined : true,
            invoiceSources:
                shouldShowKickAccountOption && !isKickSelected
                    ? [InvoiceSource.MANUAL]
                    : undefined,
        });
    };

    const selectedCount =
        (filters.accountIds?.length ?? 0) +
        (isNoAccountSelected ? 1 : 0) +
        (isKickSelected ? 1 : 0);

    return (
        <div className={css.filter}>
            <FilterPickerList
                items={items}
                value={value}
                onValueChange={handleValueChange}
                filter={matchesSearch}
                onInputValueChange={setSearch}
                defaultExpandedValues={entityValues}
                emptyLabel="No matching accounts"
                selectedCount={isSingleSelect ? 0 : selectedCount}
                onDeselect={() =>
                    onChange({
                        accountIds: undefined,
                        hasNoFinancialAccount: undefined,
                        invoiceSources: undefined,
                    })
                }
                onReverse={reverseSelection}
            />
        </div>
    );
};
